
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trash2, AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GazetteRules = () => {
    const { token, limit } = useAuth();
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCreate, setOpenCreate] = useState(false);

    // New Rule Form
    const [query, setQuery] = useState('');
    const [ruleType, setRuleType] = useState('EXACT');
    const [classes, setClasses] = useState('');

    useEffect(() => {
        fetchRules();
    }, [token]);

    const fetchRules = async () => {
        try {
            const res = await axios.get(`${API}/gazette/rules`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRules(res.data);
        } catch (error) {
            toast.error('Error al cargar reglas');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Seguro que deseas desactivar esta regla?')) return;
        try {
            await axios.delete(`${API}/gazette/rules/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Regla desactivada');
            fetchRules();
        } catch (error) {
            toast.error('Error al desactivar regla');
        }
    };

    const handleCreate = async () => {
        if (!query) return toast.error("El texto de búsqueda es obligatorio");

        try {
            const payload = {
                query,
                rule_type: ruleType,
                classes: classes ? classes.split(',').map(c => c.trim()) : []
            };

            await axios.post(`${API}/gazette/rules`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success("Regla creada exitosamente");
            setOpenCreate(false);
            setQuery('');
            setClasses('');
            fetchRules();

        } catch (error) {
            if (error.response?.status === 402) {
                toast.error("Has alcanzado el límite de tu plan. Actualiza para crear más reglas.");
            } else {
                toast.error("Error al crear regla");
            }
        }
    };

    return (
        <>
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Reglas de Vigilancia</h1>
                        <p className="text-muted-foreground">Configura qué marcas quieres monitorear en la Gaceta.</p>
                    </div>

                    <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="mr-2 h-4 w-4" /> Nueva Regla
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Crear Regla de Vigilancia</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label>Texto a buscar</Label>
                                    <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Ej. NIKE" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Tipo de Coincidencia</Label>
                                    <Select value={ruleType} onValueChange={setRuleType}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="EXACT">Exacta</SelectItem>
                                            <SelectItem value="KEYWORD">Contiene Palabra</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Clases (Opcional, separadas por coma)</Label>
                                    <Input value={classes} onChange={e => setClasses(e.target.value)} placeholder="Ej. 25, 35" />
                                </div>

                                {/* Limit Warning Hint */}
                                <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                                    <p>Reglas activas: {rules.length}</p>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button onClick={handleCreate}>Guardar</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                <Card>
                    <CardContent className="pt-6">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Texto (Query)</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Clases</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rules.map((rule) => (
                                    <TableRow key={rule.id}>
                                        <TableCell className="font-medium">{rule.query}</TableCell>
                                        <TableCell><Badge variant="outline">{rule.rule_type}</Badge></TableCell>
                                        <TableCell>{rule.classes.join(', ') || 'Todas'}</TableCell>
                                        <TableCell>
                                            {rule.active ? <span className="text-green-600 text-sm">Activa</span> : <span className="text-gray-400 text-sm">Inactiva</span>}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button size="icon" variant="ghost" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(rule.id)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {rules.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                            No tienes reglas configuradas.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </>
    );
};

// Simple Badge component shim if not available in ui/badge yet, or import it
const Badge = ({ children, variant }) => {
    const styles = variant === 'outline' ? 'border border-gray-200 text-gray-600' : 'bg-gray-100 text-gray-900';
    return <span className={`px-2 py-0.5 rounded text-xs ${styles}`}>{children}</span>;
}
